import type { ReactNode } from "react";
import { Link } from "react-router";
import {
  ArrowUpRight,
  CircleAlert,
  Wallet,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { brandFillClass } from "@/components/ui";

interface WalletHeroCardProps {
  label: string;
  /** Pre-formatted amount (absolute value). */
  amount: string;
  /** Negative balance: the user owes the platform. */
  isAmountOwed: boolean;
  /** Optional shortcut to the wallet page (omitted for platform-wide totals). */
  link?: { to: string; label: string };
  /** Small chips under the amount (e.g. "12 transactions (30d)"). */
  meta?: ReactNode;
  /** Overrides the default wallet / warning icon. */
  icon?: LucideIcon;
  className?: string;
}

/** Brand-gradient KPI tile used as the lead card of role dashboards, wallet and points. */
export function WalletHeroCard({
  label,
  amount,
  isAmountOwed,
  link,
  meta,
  icon,
  className,
}: WalletHeroCardProps) {
  const Icon = icon ?? (isAmountOwed ? CircleAlert : Wallet);

  return (
    <div
      className={cn(
        "group shadow-elevated animate-slide-up relative isolate flex h-full min-h-[152px] flex-col justify-between overflow-hidden rounded-xl p-5 text-white",
        brandFillClass(isAmountOwed ? "warning" : "primary"),
        className,
      )}
    >
      <div
        className="pointer-events-none absolute inset-0 -z-10"
        aria-hidden
      >
        <div className="absolute -top-20 -right-16 h-56 w-56 rounded-full bg-white/15 blur-3xl" />
        <div className="bg-accent-400/30 absolute -bottom-24 left-1/3 h-48 w-48 rounded-full blur-3xl" />
        <div
          className="absolute inset-0 opacity-[0.12]"
          style={{
            backgroundImage:
              "radial-gradient(rgb(255 255 255 / 0.6) 1px, transparent 1px)",
            backgroundSize: "16px 16px",
            maskImage: "linear-gradient(120deg, transparent 30%, black 100%)",
          }}
        />
      </div>

      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/15 ring-1 ring-white/25 backdrop-blur ring-inset">
            <Icon
              className="h-4 w-4"
              aria-hidden
            />
          </span>
          <p className="text-[13px] font-medium text-white">{label}</p>
        </div>
        {link ? (
          <Link
            to={link.to}
            className="inline-flex h-7 items-center gap-1 rounded-md bg-white/15 px-2 text-xs font-medium text-white ring-1 ring-white/20 transition-colors ring-inset hover:bg-white/25"
          >
            {link.label}
            <ArrowUpRight
              className="h-3.5 w-3.5 transition-transform group-hover:translate-x-px group-hover:-translate-y-px"
              aria-hidden
            />
          </Link>
        ) : null}
      </div>

      <div className="mt-6">
        <p className="text-3xl font-semibold tracking-tight tabular-nums sm:text-4xl">
          {amount}
        </p>
        {meta ? (
          <div className="mt-2 flex flex-wrap items-center gap-1.5">{meta}</div>
        ) : null}
      </div>
    </div>
  );
}

export function WalletHeroChip({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex items-center rounded-md bg-white/15 px-2 py-0.5 text-xs font-medium text-white tabular-nums ring-1 ring-white/20 ring-inset">
      {children}
    </span>
  );
}
